/**
 * Feature Flags
 *
 * Runtime-toggled features injected via window.__APP_CONFIG__.FEATURES.
 * The container entrypoint (run-service-podman.sh) generates config.js
 * from environment variables.
 *
 * Local dev: all features enabled by default.
 * Container: set FEATURE_ASSISTANT=false (etc.) to disable per environment.
 *
 * ⚠️ REGISTERED IN RFC-0503 §"Runtime feature flags". Adding a flag here is
 * three edits, not one: a default below, a line in `run-service-podman.sh`
 * (without it the flag is a local-dev toy — no environment can set it), and a
 * row in that RFC table (without it nobody else can find it).
 *
 * These are NOT the RFC-0503 divergence registry. That one is `appConfig`:
 * build-time, varies by FLAVOUR, answers "does this product have it".
 * These are runtime, vary by ENVIRONMENT, and answer "is it on here".
 */

// The global is declared in attain-ui-lib as AppConfigShape & Record<string,
// unknown>; cast through to a narrow shape so the FEATURES key remains
// typed instead of falling out as ``unknown``.
import { appConfig } from './appConfig';

const flags = ((window.__APP_CONFIG__ ?? {}) as { FEATURES?: Record<string, boolean> })
  .FEATURES ?? {};

export const features = {
  /** AI Assistant panel — kill switch for the whole feature.
   *  Runtime: ``FEATURE_ASSISTANT=false`` in the container env.
   *  Default: enabled. */
  assistant: flags.assistant ?? true,

  /** Live / Debug view toggle on the AI Assistant chat header.
   *  When ``true``, users see a Live ↔ Debug pill button next to the
   *  history icon and can opt into the expandable Request/Response
   *  detail panes. When ``false`` (production default), Live view is
   *  forced and tool-call internals are not exposed.
   *  Runtime: ``FEATURE_ASSISTANT_DEBUG_MODE=true`` in the container env. */
  assistantDebugMode: flags.assistant_debug_mode ?? false,

  /** The Workspace — case browser and case surface (RFC-0206 Surface 1/2).
   *
   *  ⚠️ DEFAULT OFF, deliberately. An environment that sets nothing keeps the
   *  product it has today, so enabling the Workspace is an explicit act per
   *  environment: it can run on beta while production is untouched. That
   *  matters while a customer is testing in production.
   *
   *  This is the RUNTIME half of a two-part gate — see `isWorkspaceEnabled()`
   *  below. Never read this flag on its own.
   *
   *  Runtime: ``FEATURE_WORKSPACE=true`` in the container env. */
  workspace: flags.workspace ?? false,

  /** Two-pane case workspace — files left, workflows right (RFC-0206 A1, W12).
   *
   *  ``false`` (default) renders the single-column case workspace that shipped
   *  in W4/W5: documents only, with Workflows as a separate route.
   *  ``true`` renders the two-pane layout: the case's documents and the work
   *  items over them side by side, with ``+ New Workflow`` in the right pane.
   *
   *  WHY THIS IS A FLAG AND NOT A REPLACEMENT. Two-pane is the intended
   *  direction — RFC-0206 §"The case workspace is two-pane" argues it, and the
   *  single-column layout is the thing it corrects (three surfaces shipped as
   *  searchable card lists in a centred column, so the workspace read as the
   *  dashboard). The flag exists to decide the layout *by using both*, which is
   *  the only honest way to settle a taste question, and to keep that
   *  comparison on ONE branch: a parallel branch would have forked the six
   *  files both layouts touch, and attain#81 already showed what a divergent UI
   *  branch costs (488 commits stale, unrebasable, salvaged by extraction).
   *
   *  ⚠️ TEMPORARY BY CONSTRUCTION. When the comparison is settled this flag and
   *  the losing layout are DELETED — not left switchable. A permanent toggle
   *  between two information architectures is two products.
   *
   *  Runtime: ``FEATURE_WORKSPACE_TWO_PANE=true`` in the container env. */
  workspaceTwoPane: flags.workspace_two_pane ?? false,
};

/**
 * Is the Workspace available — for this build, this environment, and THIS FIRM?
 *
 * THREE GATES. They answer different questions and a caller that checks one is
 * wrong in a way that surfaces somewhere else entirely:
 *
 *   appConfig.workspace.enabled   build FLAVOUR — does this product contain the
 *                                 surface? Self-service does not (RFC-0206 D6).
 *                                 A CEILING: nothing below can grant it.
 *   features.workspace            ENVIRONMENT — may any firm here have it?
 *                                 Off by default, so production stays as it is
 *                                 until someone says otherwise.
 *   org override                  THIS FIRM — RFC-0500 A5. Tri-state.
 *
 * ⚠️ THE ORG VALUE IS TRI-STATE, AND THAT IS THE WHOLE DESIGN.
 *   true       this firm has it, even where the environment default is off
 *   false      this firm does not, even where the environment default is on
 *   null/undef NOT CONFIGURED → the environment default decides
 *
 * Treating `undefined` as `false` would silently switch the Workspace off for
 * every firm nobody has configured yet — which, on the day the column ships, is
 * all of them. The migration adds NULLs precisely so nothing changes.
 *
 * ⚠️ SYNCHRONOUS, DELIBERATELY. The org value rides in on the session
 * (`user.features`), which the router already waits for. Fetching it instead
 * would make `RequireWorkspace` async and put a loading state — or a flash of
 * the dashboard — on every guarded route. See RFC-0206 D8.
 */
export function isWorkspaceEnabled(user?: { features?: { workspace?: boolean | null } } | null): boolean {
  // The flavour ceiling first: no runtime or per-org value can grant a surface
  // this build does not contain.
  if (!appConfig.workspace.enabled) return false;

  const org = user?.features?.workspace;
  // Explicit per-firm decision wins over the environment, in both directions.
  if (org === true || org === false) return org;

  // Unset — the firm has no override, so the environment decides.
  return features.workspace;
}

/**
 * Is `wcfg` offered to THIS FIRM? (RFC-0511 B — the operator switch.)
 *
 * TWO GATES, one of which is not here. The ENVIRONMENT gate is already applied
 * upstream: `GET /v1/workflows` intersects the engine catalogue with ss-api's
 * registry `enabled_in`, so a wcfg absent from this environment never reaches
 * the caller at all. This function applies the second — the firm's per-org set
 * — and it can only NARROW that list. It is never asked whether to ADD
 * something the catalogue did not return, which is what keeps a per-org value
 * from becoming a way to run a workflow the environment never registered.
 *
 * ⚠️ UNSET IS NOT OFF, AND THAT IS THE WHOLE DESIGN.
 *   undefined/null   NO per-org set → the environment decides → true. This is
 *                    what every firm holds on the day the column ships, so
 *                    nothing changes for anyone when it does.
 *   []               an explicit empty set → false for every workflow.
 *   ['a', 'b']       exactly these.
 * Treating `undefined` as an empty set would silently withdraw every workflow
 * from every firm nobody has configured yet — the same mistake
 * `isWorkspaceEnabled` above exists to avoid, with a bigger blast radius.
 *
 * ⚠️ THE GATE IS THE AUDIENCE, NEVER `account_type`. This reads the acting
 * ORGANISATION's set. A guest carries `account_type: 'guest'` while working
 * inside a host firm and is a member of that firm's audience for this purpose,
 * so they see exactly what the firm that invited them sees (RFC-0510 W42).
 *
 * ⚠️ SYNCHRONOUS, DELIBERATELY — the set rides in on the session, like the
 * Workspace override above.
 */
export function isWorkflowEnabledForOrg(
  user: { features?: { workflows?: string[] | null } } | null | undefined,
  wcfg: string,
): boolean {
  const set = user?.features?.workflows;
  if (set === undefined || set === null) return true;
  return set.includes(wcfg);
}
